import { useMemo } from 'react';
import { Lock, Play, Sparkles, Infinity as InfinityIcon, Crown } from 'lucide-react';
import { LEVELS, WORLDS, ENDLESS_UNLOCK_LEVEL, MASTER_UNLOCK_LEVEL } from '@/lib/mindOverCityLevels';
import StarBadge from './StarBadge';

/**
 * LevelSelect — Apple-Arcade-style map. 3 worlds, 30 levels, per-level star display.
 * Also shows Endless Flow / Master Flow unlock tiles.
 *
 * props:
 *   moc       — read from loungeMeta.moc (levelStars, highestUnlocked, modesUnlocked)
 *   onPick    — (levelId) => void
 *   onEndless — () => void
 *   onMaster  — () => void
 */
export default function LevelSelect({ moc, onPick, onEndless, onMaster }) {
  const grouped = useMemo(() => WORLDS.map(w => ({
    ...w,
    levels: LEVELS.filter(l => l.world === w.id),
  })), []);

  const highestUnlocked = moc?.highestUnlocked ?? 1; // gated: clear a level to unlock the next
  const endlessUnlocked = true;
  const masterUnlocked  = true;

  return (
    <div className="space-y-6" data-testid="moc-level-select">
      {grouped.map((w) => (
        <div key={w.id} className="rounded-2xl p-4 lg:p-5"
             style={{ background: `linear-gradient(135deg, ${w.color} 0%, ${adjust(w.color, -10)} 100%)` }}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: w.accent }}>
                World {w.id}
              </p>
              <h4 className="font-heading text-xl text-white mt-0.5">{w.name}</h4>
              <p className="text-white/55 text-xs mt-0.5">{w.tagline}</p>
            </div>
            <div className="text-right">
              <p className="text-[9px] uppercase tracking-widest text-white/40">Stars</p>
              <p className="font-heading text-lg text-white tabular-nums">
                {w.levels.reduce((sum, l) => sum + (moc?.levelStars?.[l.id] ?? 0), 0)}
                <span className="text-white/40">/{w.levels.length * 3}</span>
              </p>
            </div>
          </div>

          <div className="grid grid-cols-5 gap-2">
            {w.levels.map((l) => {
              const stars = moc?.levelStars?.[l.id] ?? 0;
              const locked = l.id > highestUnlocked;
              return (
                <button
                  key={l.id}
                  type="button"
                  disabled={locked}
                  onClick={() => onPick(l.id)}
                  data-testid={`moc-level-${l.id}`}
                  className={`relative rounded-xl px-2 py-2.5 text-center transition-all border ${
                    locked
                      ? 'bg-white/5 border-white/5 cursor-not-allowed'
                      : stars > 0
                        ? 'bg-white/10 border-white/15 hover:bg-white/15 hover:-translate-y-0.5'
                        : 'bg-white/[0.06] border-white/10 hover:bg-white/10 hover:-translate-y-0.5'
                  }`}
                >
                  <p className={`font-heading text-lg tabular-nums leading-none ${locked ? 'text-white/25' : 'text-white'}`}>
                    {l.id}
                  </p>
                  <div className="mt-1 flex items-center justify-center min-h-[14px]">
                    {locked ? (
                      <Lock size={11} className="text-white/25" strokeWidth={2}/>
                    ) : (
                      <StarBadge count={stars} size={9} testid={`moc-level-${l.id}-stars`}/>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {/* Mode unlocks — Endless & Master */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={onEndless}
          data-testid="moc-endless-mode"
          className="rounded-2xl p-4 text-left transition-all border bg-gradient-to-br from-[#0F1A2E] to-[#1A2B4A] border-gold/30 hover:border-gold/60 hover:-translate-y-0.5"
        >
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] font-semibold text-gold flex items-center gap-1.5">
                <InfinityIcon size={12} strokeWidth={2}/> Endless Flow
              </p>
              <p className="font-heading text-lg mt-1 text-white">Play as long as you can</p>
              <p className="text-white/60 text-xs mt-1.5 leading-relaxed">
                No timer, no gates — just you, the sky, and your rhythm. Every orb keeps counting until you crash.
              </p>
            </div>
            <Play size={16} className="text-gold mt-1 shrink-0" strokeWidth={2}/>
          </div>
          {(moc?.endlessBest ?? 0) > 0 && (
            <p className="text-white/60 text-xs mt-2">Best: <span className="text-gold tabular-nums">{moc.endlessBest}</span> orbs</p>
          )}
        </button>

        <button
          type="button"
          onClick={onMaster}
          data-testid="moc-master-mode"
          className="rounded-2xl p-4 text-left transition-all border bg-gradient-to-br from-[#0B1E29] to-[#134058] border-gold/30 hover:border-gold/60 hover:-translate-y-0.5"
        >
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] font-semibold text-gold flex items-center gap-1.5">
                <Crown size={12} strokeWidth={2}/> Master Flow
              </p>
              <p className="font-heading text-lg mt-1 text-white">The hardest challenge</p>
              <p className="text-white/60 text-xs mt-1.5 leading-relaxed">
                Wind, tight gates, and every variant at once. Reserved for players ready to be tested.
              </p>
            </div>
            <Sparkles size={16} className="text-gold mt-1 shrink-0" strokeWidth={2}/>
          </div>
          {(moc?.masterBest ?? 0) > 0 && (
            <p className="text-white/60 text-xs mt-2">Best: <span className="text-gold tabular-nums">{moc.masterBest}</span> orbs</p>
          )}
        </button>
      </div>
    </div>
  );
}

/** Tiny hex-to-adjusted helper to shift lightness of the world background */
function adjust(hex, amt) {
  const h = hex.replace('#', '');
  const num = parseInt(h, 16);
  let r = (num >> 16) + amt;
  let g = ((num >> 8) & 0xff) + amt;
  let b = (num & 0xff) + amt;
  r = Math.max(0, Math.min(255, r));
  g = Math.max(0, Math.min(255, g));
  b = Math.max(0, Math.min(255, b));
  return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
}
